import React, { useState, useEffect } from 'react';
import { initializeMsal, login, logout, isAuthenticated, getCurrentUser } from './services/authService';
import { listItems, downloadFile, uploadFile, uploadLargeFile, buildPath } from './services/sharepointService';
import { folderStructure, months, years } from './config/authConfig';
import './App.css';
import CalculadoraRetenciones from './components/CalculadoraRetenciones';
import SolicitudIMSS from './components/SolicitudIMSS';
import ListaSolicitudes from './components/ListaSolicitudes';

function App() {
    const [loading, setLoading] = useState(true);
    const [authenticated, setAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [currentPath, setCurrentPath] = useState([]);
    const [items, setItems] = useState([]);
    const [loadingItems, setLoadingItems] = useState(false);
    const [notification, setNotification] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [showCalculadora, setShowCalculadora] = useState(false);
    const [showSolicitudIMSS, setShowSolicitudIMSS] = useState(false);
    const [showListaSolicitudes, setShowListaSolicitudes] = useState(false);
    const [solicitudesIMSS, setSolicitudesIMSS] = useState([]);



    // Inicializar MSAL al cargar
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const initialize = async () => {
            try {
                await initializeMsal();
                const isAuth = isAuthenticated();
                setAuthenticated(isAuth);
                
                if (isAuth) {
                    const currentUser = getCurrentUser();
                    setUser(currentUser);
                    await loadItems([]);
                }
            } catch (error) {
                console.error("Error inicializando:", error);
            } finally {
                setLoading(false);
            }
        };
        
        initialize();
    }, []);

    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 4000);
    };

const handleSubmitSolicitud = async (solicitud, archivos) => {
    console.log('🚀 VERSION NUEVA - handleSubmitSolicitud iniciado');
    console.log('Solicitud:', solicitud);
    console.log('Archivos:', archivos);
    try {
        const nombreEmpresaLimpio = (solicitud.cliente || 'SIN_NOMBRE')
            .replace(/[^a-zA-Z0-9_\s]/g, '')  // Eliminar caracteres especiales
            .replace(/\s+/g, '_')              // Reemplazar espacios con _
            .toUpperCase();                    // Convertir a mayúsculas
    
        const carpetaSolicitudes = `SOLICITUDES_IMSS/${nombreEmpresaLimpio}`;
        
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const nombreSolicitud = `${timestamp}_${solicitud.tipo}_${solicitud.datos.nombreCompleto.replace(/\s+/g, '_')}`;
        
        const jsonData = JSON.stringify(solicitud, null, 2);
        const jsonBlob = new Blob([jsonData], { type: 'application/json' });
        const jsonFile = new File([jsonBlob], `${nombreSolicitud}.json`);
        
        const pathJSON = `${carpetaSolicitudes}/${nombreSolicitud}.json`;
        await uploadFile(jsonFile, pathJSON);
        
        for (const archivo of archivos) {
            const pathArchivo = `${carpetaSolicitudes}/${nombreSolicitud}_${archivo.name}`;
            await uploadFile(archivo, pathArchivo);
        }
        
        setSolicitudesIMSS([solicitud, ...solicitudesIMSS]);
        setShowSolicitudIMSS(false);
        showNotification('Solicitud enviada correctamente. Nos pondremos en contacto pronto.');
        
    } catch (error) {
        console.error('Error al enviar solicitud:', error);
        showNotification('Error al enviar la solicitud. Intente de nuevo.', 'error');
    }
};

    const handleLogin = async () => {
        try {
            setLoading(true);
            const response = await login();
            setAuthenticated(true);
            setUser(response.account);
            showNotification('¡Bienvenido! Has iniciado sesión correctamente');
            await loadItems([]);
        } catch (error) {
            console.error('Error en login:', error);
            showNotification('Error al iniciar sesión. Intenta de nuevo.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        setAuthenticated(false);
        setUser(null);
        setCurrentPath([]);
        setItems([]);
        showNotification('Sesión cerrada correctamente', 'info');
    };

    const loadItems = async (path) => {
        setLoadingItems(true);
        try {
            const pathString = buildPath(path);
            const result = await listItems(pathString);
            setItems(result);
            setCurrentPath(path);
        } catch (error) {
            console.error('Error cargando items:', error);
            showNotification('Error cargando documentos', 'error');
            setItems([]);
        } finally {
            setLoadingItems(false);
        }
    };

    const handleNavigate = (folderName) => {
        const newPath = [...currentPath, folderName];
        loadItems(newPath);
    };

    const handleNavigateBack = (index) => {
        const newPath = currentPath.slice(0, index);
        loadItems(newPath);
    };

    const handleDownload = async (item) => {
        try {
            showNotification(`Descargando: ${item.name}...`, 'info');
            await downloadFile(item.id, item.name);
            showNotification(`${item.name} descargado exitosamente`);
        } catch (error) {
            showNotification('Error al descargar el archivo', 'error');
        }
    };

    const handleUpload = async (event) => {
        const files = event.target.files;
        if (files.length === 0) return;

        const currentPathString = buildPath(currentPath);
        
        try {
            setUploadProgress(0);
            
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                showNotification(`Subiendo: ${file.name}...`, 'info');
                
                // Si el archivo es mayor a 4MB, usar uploadLargeFile
                if (file.size > 4 * 1024 * 1024) {
                    await uploadLargeFile(currentPathString, file);
                } else {
                    await uploadFile(currentPathString, file);
                }
                
                setUploadProgress(((i + 1) / files.length) * 100);
            }
            
            showNotification(`${files.length} archivo(s) subido(s) correctamente`);
            setUploadProgress(null);
            
            // Recargar la lista
            await loadItems(currentPath);
            
            // Limpiar el input
            event.target.value = '';
        } catch (error) {
            console.error('Error subiendo archivos:', error);
            showNotification('Error al subir archivos', 'error');
            setUploadProgress(null);
        }
    };

    const getCurrentView = () => {
        // Si estamos en la raíz, mostrar las carpetas principales de SharePoint
        if (currentPath.length === 0) {
            return items.filter(item => item.folder);
        }
        
        const mainFolder = currentPath[0];
        const config = folderStructure[mainFolder];
        
        if (!config) {
            // Si no hay config, mostrar items reales de SharePoint
            return items;
        }
        
        // Nivel 1: Subcarpetas
        if (currentPath.length === 1) {
            if (config.hasSubfolders) {
                // Generar subcarpetas virtuales
                return config.subfolders.map(name => ({
                    name,
                    folder: {},
                    virtual: true
                }));
            } else if (config.yearMonth) {
                // Generar años virtuales
                return years.map(name => ({
                    name,
                    folder: {},
                    virtual: true
                }));
            }
        }
        
        // Nivel 2: Para carpetas con subcarpetas, mostrar años
        if (currentPath.length === 2 && config.hasSubfolders) {
            // Generar años virtuales
            return years.map(name => ({
                name,
                folder: {},
                virtual: true
            }));
        }
        
        // Nivel 2 o 3: Para carpetas de año, mostrar meses
        const shouldShowMonths = 
            (currentPath.length === 2 && !config.hasSubfolders && config.yearMonth) ||
            (currentPath.length === 3 && config.hasSubfolders);
        
        if (shouldShowMonths) {
            // Generar meses virtuales
            return months.map(name => ({
                name,
                folder: {},
                virtual: true
            }));
        }
        
        // En el nivel final o cuando hay items reales, mostrar items de SharePoint
        return items;
    };

    const getColorClass = (name) => {
        for (const [key, value] of Object.entries(folderStructure)) {
            if (name.includes(key)) {
                const colorMap = {
                    'yellow': 'bg-yellow-100 text-yellow-600',
                    'blue': 'bg-blue-100 text-blue-600',
                    'orange': 'bg-orange-100 text-orange-600',
                    'green': 'bg-green-100 text-green-600',
                    'indigo': 'bg-indigo-100 text-indigo-600',
                    'purple': 'bg-purple-100 text-purple-600',
                    'violet': 'bg-violet-100 text-violet-600'
                };
                return colorMap[value.color] || 'bg-gray-100 text-gray-600';
            }
        }
        return 'bg-gray-100 text-gray-600';
    };

    if (loading) {
        return (
            <div className="loading-screen">
                <div className="spinner"></div>
                <p>Cargando Portal de Clientes...</p>
            </div>
        );
    }

    if (!authenticated) {
        return (
            <div className="login-screen">
                <div className="login-container">
                    <div className="login-logo">
                        <div className="logo-icon">
                            <i className="fas fa-calculator"></i>
                        </div>
                        <h1>Portal de Clientes</h1>
                        <p>SO CONSULTORES SAS</p>
                    </div>
                    
                    <div className="login-card">
                        <h2>Bienvenido</h2>
                        <p className="login-subtitle">Inicia sesión con tu cuenta de Microsoft</p>
                        
                        <button onClick={handleLogin} className="login-button">
                            <svg className="microsoft-icon" viewBox="0 0 23 23">
                                <path fill="#f25022" d="M0 0h11v11H0z"/>
                                <path fill="#00a4ef" d="M12 0h11v11H12z"/>
                                <path fill="#7fba00" d="M0 12h11v11H0z"/>
                                <path fill="#ffb900" d="M12 12h11v11H12z"/>
                            </svg>
                            Iniciar sesión con Microsoft
                        </button>
                        
                        <p className="login-footer">
                            <i className="fas fa-shield-alt"></i> Conexión segura y cifrada
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const currentItems = getCurrentView();

    return (
        <div className="app">
            {/* Notificaciones */}
            {notification && (
                <div className={`notification ${notification.type}`}>
                    <i className={`fas fa-${notification.type === 'success' ? 'check-circle' : notification.type === 'error' ? 'exclamation-circle' : 'info-circle'}`}></i>
                    {notification.message}
                </div>
            )}

            {/* Header */}
            <header className="header">
                <div className="header-content">
                    <div className="header-left">
                        <div className="logo">
                            <i className="fas fa-calculator"></i>
                        </div>
                        <div className="header-title">
                            <h1>Portal de Clientes</h1>
                            <p>SO CONSULTORES SAS</p>
                        </div>
                    </div>
                    <div className="header-right">
                        <div className="user-info">
                             <p className="user-name">{user?.name}</p>
                             <p className="user-email">{user?.username}</p>
                   </div>
                   <button onClick={() => setShowSolicitudIMSS(true)} className="imss-button">
        <i className="fas fa-file-medical"></i>
        <span>Solicitar Alta/Baja</span>
                   </button>
                   <button onClick={() => setShowListaSolicitudes(true)} className="mis-solicitudes-button">
                      <i className="fas fa-list"></i>
                      <span>Mis Solicitudes</span>
                   </button>
                   <button onClick={() => setShowCalculadora(!showCalculadora)} className="tools-button">
                      <i className="fas fa-calculator"></i>
                      <span>Herramientas</span>
                   </button>
                   <button onClick={handleLogout} className="logout-button">
                   <i className="fas fa-sign-out-alt"></i>
                   <span>Cerrar Sesión</span>
                   </button>
                     </div>
                    </div>
            </header>

{/* Modal de Solicitud IMSS */}
{showSolicitudIMSS && (
    <div className="calculadora-overlay" onClick={() => setShowSolicitudIMSS(false)}>
        <div className="calculadora-modal" onClick={(e) => e.stopPropagation()}>
            <SolicitudIMSS 
                user={user}
                onClose={() => setShowSolicitudIMSS(false)}
                onSubmit={handleSubmitSolicitud}
            />
        </div>
    </div>
)}

{/* Modal de Lista de Solicitudes */}
{showListaSolicitudes && (
    <div className="calculadora-overlay" onClick={() => setShowListaSolicitudes(false)}>
        <div className="calculadora-modal" onClick={(e) => e.stopPropagation()}>
            <ListaSolicitudes 
                user={user}
                solicitudes={solicitudesIMSS}
                onClose={() => setShowListaSolicitudes(false)}
            />
        </div>
    </div>
)}

{/* Calculadora Modal */}
{showCalculadora && (
    <div className="calculadora-overlay" onClick={() => setShowCalculadora(false)}>
        <div className="calculadora-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={() => setShowCalculadora(false)}>
                <i className="fas fa-times"></i>
            </button>
            <CalculadoraRetenciones />
        </div>
    </div>
)}

             {/* Calculadora Modal */}
             {showCalculadora && (
             <div className="calculadora-overlay" onClick={() => setShowCalculadora(false)}>
             <div className="calculadora-modal" onClick={(e) => e.stopPropagation()}>
             <button className="close-button" onClick={() => setShowCalculadora(false)}>
                <i className="fas fa-times"></i>
            </button>
            <CalculadoraRetenciones />
            </div>
            </div>
             )}

            {/* Main Content */}
            <main className="main-content">
                {/* Breadcrumb */}
                {currentPath.length > 0 && (
                    <div className="breadcrumb">
                        <button onClick={() => handleNavigateBack(0)} className="breadcrumb-item">
                            <i className="fas fa-home"></i> Inicio
                        </button>
                        {currentPath.map((item, index) => (
                            <React.Fragment key={index}>
                                <i className="fas fa-chevron-right breadcrumb-separator"></i>
                                <button 
                                    onClick={() => handleNavigateBack(index + 1)}
                                    className="breadcrumb-item"
                                >
                                    {item}
                                </button>
                            </React.Fragment>
                        ))}
                    </div>
                )}

                {/* Upload Section */}
                <div className="upload-section">
                    <label className="upload-button">
                        <i className="fas fa-cloud-upload-alt"></i>
                        <span>Subir Documentos</span>
                        <input 
                            type="file" 
                            multiple 
                            onChange={handleUpload}
                            style={{ display: 'none' }}
                        />
                    </label>
                    {uploadProgress !== null && (
                        <div className="upload-progress">
                            <div className="progress-bar">
                                <div 
                                    className="progress-fill" 
                                    style={{ width: `${uploadProgress}%` }}
                                ></div>
                            </div>
                            <span>{Math.round(uploadProgress)}%</span>
                        </div>
                    )}
                </div>

                {/* Items List */}
                <div className="items-container">
                    <div className="items-header">
                        <h2>
                            <i className="fas fa-folder"></i>
                            {currentPath.length > 0 ? currentPath[currentPath.length - 1] : 'Documentos'}
                        </h2>
                        <span className="items-count">{currentItems.length} elementos</span>
                    </div>

                    {loadingItems ? (
                        <div className="loading-items">
                            <div className="spinner"></div>
                            <p>Cargando...</p>
                        </div>
                    ) : currentItems.length === 0 ? (
                        <div className="empty-state">
                            <i className="fas fa-inbox"></i>
                            <p>Esta carpeta está vacía</p>
                        </div>
                    ) : (
                        <div className="items-grid">
                            {currentItems.map((item, index) => (
                                <div 
                                    key={item.id || index} 
                                    className="item-card"
                                    onClick={() => item.folder && handleNavigate(item.name)}
                                >
                                    <div className={`item-icon ${item.folder ? getColorClass(item.name) : 'bg-red-100 text-red-600'}`}>
                                        <i className={`fas fa-${item.folder ? 'folder' : 'file-pdf'}`}></i>
                                    </div>
                                    <div className="item-info">
                                        <p className="item-name">{item.name}</p>
                                        {!item.folder && (
                                            <div className="item-meta">
                                                <span>{item.size ? (item.size / 1024 / 1024).toFixed(2) + ' MB' : ''}</span>
                                                {item.lastModifiedDateTime && (
                                                    <span>{new Date(item.lastModifiedDateTime).toLocaleDateString('es-MX')}</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {!item.folder && (
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDownload(item);
                                            }}
                                            className="download-button"
                                        >
                                            <i className="fas fa-download"></i>
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            {/* Footer */}
            <footer className="footer">
                <p>© 2024 SO CONSULTORES SAS. Todos los derechos reservados.</p>
                <p className="footer-secure">
                    <i className="fas fa-shield-alt"></i> Portal seguro protegido con cifrado SSL
                </p>
            </footer>
        </div>
    );
}

export default App;
